/**
 * Where a call goes (host-aware routing, the maintainer's option 2 for a node-served page): every route to a backend
 * that serves it (`RouteDef.hosts`).
 *
 *   the primary serves it                        the primary — a route both hosts serve stays on the data path
 *                                                (the node when on the studio LAN, PLAN §10.2)
 *   the primary is a node, the route the cloud's straight to the cloud, over the page's own cloud endpoint and with
 *                                                the same Cognito token (PLAN §11: one JWT for both hosts; the cloud's
 *                                                CORS admits the nodes' HTTPS names, apps/cloud-api/src/cors.ts)
 *     … no cloud in the runtime config         unavailable: `no-cloud` (a lab node, a node with no link)
 *     … signed in offline                      unavailable: `offline-session` — a break-glass or PIN session is the
 *                                                node's own token (D20), which the cloud refuses
 *     … the cloud not callable from this page  unavailable: `cloud-not-callable` — development sign-in (the node's own
 *                                                tokens), or a page opened by IP, which the cloud's CORS does not admit
 *                                                (the cloud endpoint's `canBePrimary`, `endpoints.ts`)
 *     … the cloud down                         unavailable: `cloud-unreachable` (its probes say so)
 *   the primary is the cloud, the route a node's unavailable: `node-only` (a pair's own routes)
 *
 * Unavailable means unsent: the caller gets `RouteUnavailableError` at once (`failover.ts`), and a screen asks first
 * (`useRouteAvailability`) to say "needs a cloud connection" and why, rather than fire requests that would fail. No
 * node proxies the cloud (PLAN §13's backlog has it, for operator PCs with no internet).
 */
import { routeServedBy, type RouteDef } from '@norna/api-contract/routes.js';
import type { Endpoint } from './endpoints.js';
import type { ConnectivityState } from './state.js';

/** How the page is signed in (`Session['provider']`), or null (signed out: nothing is sent anywhere). */
export type SessionKind = 'cognito' | 'dev' | 'local' | null;

export type RouteUnavailableReason = 'no-cloud' | 'offline-session' | 'cloud-not-callable' | 'cloud-unreachable' | 'node-only';

export type RouteTarget = { readonly ok: true; readonly endpoint: Endpoint } | { readonly ok: false; readonly reason: RouteUnavailableReason };

/** The backend that serves `route` from this page now (the module comment's table). */
export function routeTarget(route: RouteDef, state: Pick<ConnectivityState, 'endpoints' | 'primaryId'>, session: SessionKind): RouteTarget {
  const primary = (state.endpoints.find((s) => s.endpoint.id === state.primaryId) ?? state.endpoints[0])?.endpoint;
  if (primary === undefined) return { ok: false, reason: 'no-cloud' };
  if (routeServedBy(route, primary.kind)) return { ok: true, endpoint: primary };
  if (primary.kind === 'cloud') return { ok: false, reason: 'node-only' };
  const cloud = state.endpoints.find((s) => s.endpoint.kind === 'cloud');
  if (cloud === undefined) return { ok: false, reason: 'no-cloud' };
  if (session === 'local') return { ok: false, reason: 'offline-session' };
  if (!cloud.endpoint.canBePrimary) return { ok: false, reason: 'cloud-not-callable' };
  if (cloud.reach === 'down') return { ok: false, reason: 'cloud-unreachable' };
  return { ok: true, endpoint: cloud.endpoint };
}

/** Why a cloud-only screen or control is not available here, for people: it sits under a title naming what. */
export function unavailableText(reason: RouteUnavailableReason): string {
  switch (reason) {
    case 'no-cloud':
      return 'It is kept by the cloud, and this studio server runs without a cloud connection.';
    case 'offline-session':
      return 'It is kept by the cloud, and you are signed in offline (this server’s emergency account or your offline PIN), which the cloud does not accept. Sign in with your account once the cloud is reachable.';
    case 'cloud-not-callable':
      return 'It is kept by the cloud, which this page cannot call: open the server by its HTTPS name (n-….local…), or use the cloud portal.';
    case 'cloud-unreachable':
      return 'It is kept by the cloud, which this browser cannot reach right now. It comes back here as soon as the cloud answers again.';
    case 'node-only':
      return 'It is a studio server’s own: open it from the server.';
  }
}
